import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, ListGroup, ListGroupItem, Input, Spinner, Badge, InputGroup, InputGroupText
} from "reactstrap";
import { connect } from "react-redux";
import { updateGroup } from "../../redux/Messages/ThreadsAction";
import {
  loadCoachesPage,
  loadStudentsPage,
  usersReset
} from "../../redux/Messages/UsersPrepTalkAction";
import { normEmail } from "../../util/email";
import { addSystemMessage } from "../../redux/Messages/MessagesAction";
import { getUserDataByEmails } from "../../redux/Messages/UsersPrepTalkAction";

function GroupMembersModal({
  isOpen, toggle, thread, meEmail,
  coaches, students,
  loadingCoaches, loadingStudents,
  hasMoreCoaches, hasMoreStudents,
  loadCoaches, loadStudents, resetUsers,
  dispatch, presenceItems, readOnly
}) {
  const [name, setName] = useState(thread?.name || "Group");
  const [members, setMembers] = useState(new Set(thread?.participantsEmails || []));
  const [q, setQ] = useState("");


  const presenceMap = useMemo(
      () => Object.fromEntries((presenceItems || []).map(p => [p.email, p])),
      [presenceItems]
  );

  // when modal opens, load users
  useEffect(() => {
    if (isOpen) {
      loadCoaches({ reset: true });
      loadStudents({ reset: true });
    } else {
      resetUsers();
    }
  }, [isOpen, loadCoaches, loadStudents, resetUsers]);

  const containerRef = useRef(null), coachSentinel = useRef(null), studentSentinel = useRef(null);

  // infinite scroll for coaches/students
  useEffect(() => {
    const rootEl = containerRef.current;
    if (!rootEl) return;

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        if (e.target.id === "coachSentinel" && hasMoreCoaches && !loadingCoaches) loadCoaches();
        if (e.target.id === "studentSentinel" && hasMoreStudents && !loadingStudents) loadStudents();
      });
    }, { root: rootEl, rootMargin: "160px", threshold: 0 });

    coachSentinel.current && obs.observe(coachSentinel.current);
    studentSentinel.current && obs.observe(studentSentinel.current);

    return () => obs.disconnect();
  }, [hasMoreCoaches, hasMoreStudents, loadingCoaches, loadingStudents, loadCoaches, loadStudents]);

  const add = (email) => {
    const s = new Set(members);
    s.add(email);
    setMembers(s);
  };

  const remove = (email) => {
    const norm = normEmail(email);
    if (norm === normEmail(meEmail)) return; // prevent removing self
    const s = new Set(members);
    s.delete(norm);
    setMembers(s);
  };

  // merge & filter (like in PeopleList)
  const filterMerge = (arr = []) => {
    const qq = q.toLowerCase();
    return arr
      .filter((u) => u.email && u.email !== normEmail(meEmail))
      .map(u => ({ ...u, online: !!presenceMap[u.email]?.online }))
      .filter(
        (u) =>
          !qq ||
          (u.displayName || "").toLowerCase().includes(qq) ||
          u.email.toLowerCase().includes(qq)
      )
      .sort((a, b) =>
        (a.displayName || a.email).localeCompare(b.displayName || b.email)
      );
  };

  const rows = useMemo(
    () => filterMerge([...(coaches || []), ...(students || [])]),
    [coaches, students, meEmail, q]
  );

//   const save = async () => {
//     await dispatch(
//       updateGroup({
//         threadId: thread.id,
//         name,
//         participantsEmails: Array.from(members),
//       })
//     );
//     toggle();
//   };

    const save = async () => {
        const prevMembers = new Set(thread?.participantsEmails || []);
        const newMembers = new Set(members);

        // Detect changes
        const added = [...newMembers].filter(m => !prevMembers.has(m));
        const removed = [...prevMembers].filter(m => !newMembers.has(m));
        const nameChanged = name && name !== thread?.name;

        // Update group info in Firestore
        await dispatch(
            updateGroup({
            threadId: thread.id,
            name,
            participantsEmails: Array.from(newMembers),
            })
        );

        // Fire system messages
        if (added.length > 0) {
            for (const email of added) {
            await addSystemMessage({
                threadId: thread.id,
                event: "member_added",
                actor: meEmail,          // who added
                extra: { target: email } // who was added
            });
            }
        }

        if (removed.length > 0) {
            for (const email of removed) {
            await addSystemMessage({
                threadId: thread.id,
                event: "member_removed",
                actor: meEmail,
                extra: { target: email }
            });
            }
        }

        if (nameChanged) {
            await addSystemMessage({
            threadId: thread.id,
            event: "group_rename",
            actor: meEmail,
            extra: { oldName: thread?.name, newName: name }
            });
        }

        toggle();
    };


  const colors = [
    "bg-gradient-blue",
    "bg-gradient-indigo",
    "bg-gradient-purple",
    "bg-gradient-pink",
    "bg-gradient-red",
    "bg-gradient-teal",
    "bg-gradient-cyan",
    "bg-gradient-primary",
    "bg-gradient-info",
    "bg-gradient-danger",
  ];

  function shuffle(array) {
    let arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }


  const shuffledColors = useMemo(() => shuffle(colors), []);


  const [studentData, setStudentData] = useState([]); // { email: displayName }

  useEffect(() => {
    let cancelled = false;

    async function loadNames() {
      
      const data = await getUserDataByEmails([...members]);
        
      if (!cancelled) setStudentData(data);
    }

    loadNames();
    return () => { cancelled = true; };
  }, [members]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
      <ModalHeader toggle={toggle}>Group Settings</ModalHeader>
      <ModalBody className="pt-1">
        <div className="mb-4">
          <label className="font-weight-bold mb-2">Group name</label>
          <Input disabled={readOnly} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        
        <div className="mb-2">
          <div className="font-weight-bold mb-2">Members</div>
          <ListGroup className="mb-4">
            {Array.from(members).map((m, i) => {
              
              const peerOnline = m ? presenceMap?.[m]?.online : false;
              
              const displayName = studentData.find(data => data.email == m)?.name || m
              const profilePicture = studentData.find(data => data.email == m)?.profilePicture || ""

              return (
                <ListGroupItem
                    key={m}
                    className="d-flex justify-content-between align-items-center"
                    >
                    <div className="d-flex align-items-center gap-2">
                      <div className="position-relative">
                        { !studentData.find(data => data.email == m)?.profilePicture ? 
                            <div className={"msgr-avatar "+shuffledColors[i % shuffledColors.length]+" text-white"}><span>{(studentData.find(data => data.email == m)?.name || m ||"?").slice(0,1).toUpperCase()}</span>
                            
                            </div>
                          :
                          <img 
                                src={"https://storage.googleapis.com/pre-dental-tracker.appspot.com/profile-picture/" + studentData.find(data => data.email == m)?.profilePicture}
                                className="msgr-avatar border border-dark" 
                                alt="..." 
                                style={{
                                    objectFit:"cover"
                                }}
                            /> }
                        
                      </div>
                      <div className="ml-2">
                        <div className="font-weight-bolder text-default">{displayName}</div>
                        <div className="font-weight-bold text-xs text-muted">{m}</div>
                        <Badge color={peerOnline? "success" : "light"} className="ms-1 font-weight-bolder text-capitalize">{peerOnline ? "Online" : "Offline"}</Badge>  

                      </div>
                    </div>
                    <Button
                        size="sm"
                        color="danger"
                        onClick={() => remove(m)}
                        disabled={members.size <= 3 || readOnly}
                    >
                        Remove
                    </Button>
                </ListGroupItem>
            )})}
          </ListGroup>

          <div className="font-weight-bold mb-2">Add people</div>

          <InputGroup className="mb-2"><InputGroupText><i class="fas fa-search text-primary font-weight-bolder text-lg"></i></InputGroupText><Input placeholder="Search by name or email…" className="pl-2" value={q} onChange={(e) => setQ(e.target.value)} /></InputGroup>


          {/* <div ref={containerRef} style={{ maxHeight: 260, overflow: "auto" }}>
            <ListGroup flush>
              {rows.map((u) => (
                <ListGroupItem
                  key={u.email}
                  action
                  onClick={() => add(u.email)}
                >
                  {u.displayName || u.email}{" "}
                  <small className="text-muted">{u.email}</small>
                </ListGroupItem>
              ))}
              <div id="coachSentinel" ref={coachSentinel} />
              <div id="studentSentinel" ref={studentSentinel} />
              {(loadingCoaches || loadingStudents) && (
                <ListGroupItem className="text-center">
                  <Spinner size="sm" /> Loading…
                </ListGroupItem>
              )}
              {!loadingCoaches && !loadingStudents && rows.length === 0 && (
                <ListGroupItem className="text-muted">No matches</ListGroupItem>
              )}
            </ListGroup>
          </div> */}

          <div ref={containerRef} style={{ maxHeight: 260, overflow: "auto" }}>
            <ListGroup flush>
              {rows.map((u, i) => (
                <ListGroupItem 
                  disabled={readOnly}
                  key={u.email}
                  action
                  onClick={() => add(u.email)} 
                  className="d-flex align-items-center">

                 

                  <div className="position-relative">

                    { !u.profilePicture ? 
                    <div className={"msgr-avatar "+shuffledColors[i % shuffledColors.length]+" text-white"}><span>{(u.email||"?").slice(0,1).toUpperCase()}</span>
                    
                    </div>
                    :
                  <img 
                        src={"https://storage.googleapis.com/pre-dental-tracker.appspot.com/profile-picture/" + u.profilePicture}
                        className="msgr-avatar border border-dark" 
                        alt="..." 
                        style={{
                            objectFit:"cover"
                        }}
                    /> }


                    <span className={`position-absolute bottom-0 end-0 fas fa-circle msgr-dot  ${u.online ? 'text-success':'text-gray'}`} />

                  </div>


                  <div className="min-w-0 ml-1" style={{width:"85%"}}>
                    
                    <div className="min-w-0" >
                      
                      <div className=" text-truncate text-sm font-weight-bolder pr-2">{(u.displayName || "Student")}</div>
                        {/* {!r.isGroup && r.online && <Badge color="success" pill>Online</Badge>} */}
                        {/* {r.isGroup && <Badge color="info" pill>Group</Badge>} */}
                      <div className="text-muted small text-truncate" >{u.email}</div>
                      
                    </div>
                    <div className="d-flex align-items-center" >
                        {u.coach ? <Badge color="info" className="ms-2 text-capitalize font-weight-bolder">Coach</Badge> : <Badge color="warning" className="ms-2 text-capitalize font-weight-bolder">Student</Badge>}
                    </div>
                    
                  </div>


                  {/* <div className="flex-grow-1">
                    <div className="fw-semibold">{u.displayName || u.email}</div>
                    <div className="small text-muted">{u.email}</div>
                  </div>
                  <Badge color={u.online ? "success" : "secondary"} pill>{u.online ? "Online" : "Offline"}</Badge>
                  {u.coach ? <Badge color="info" className="ms-2">Coach</Badge> : <Badge color="light" className="ms-2 text-muted">Student</Badge>} */}
                  
                </ListGroupItem>
                
              ))}
              <div id="coachSentinel" ref={coachSentinel} />
              <div id="studentSentinel" ref={studentSentinel} />
              {(loadingCoaches || loadingStudents) && <ListGroupItem className="text-center"><Spinner size="sm" /> Loading…</ListGroupItem>}
              {!loadingCoaches && !loadingStudents && rows.length === 0 && <ListGroupItem className="text-muted">No people found</ListGroupItem>}
            </ListGroup>
          </div>

        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
        <Button color="primary" disabled={readOnly} onClick={save}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default connect(
  (s) => ({
    coaches: s.usersPreptalk.coaches,
    students: s.usersPreptalk.students,
    loadingCoaches: s.usersPreptalk.loadingCoaches,
    loadingStudents: s.usersPreptalk.loadingStudents,
    hasMoreCoaches: s.usersPreptalk.hasMoreCoaches,
    hasMoreStudents: s.usersPreptalk.hasMoreStudents,
    meEmail: s.loginAuth.userDetails?.email?.toLowerCase(),
    presenceItems: s.presence.items,
  }),
  (d) => ({
    loadCoaches: (a) => d(loadCoachesPage(a)),
    loadStudents: (a) => d(loadStudentsPage(a)),
    resetUsers: () => d(usersReset()),
    dispatch: d,
  })
)(GroupMembersModal);
